import { Link } from "@/components/LangLink";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, StaggerItem } from "@/components/primitives";
import { useContent } from "@/lib/content";

export const ProductHero = ({ theme = "navy", badge, ral, name, logo, tagline, paragraphs, layout = "stack", rightContent, leftExtra, testid }) => {
  const { COPY } = useContent();
  const split = layout === "split";

  const Badges = (
    <div className="flex flex-wrap items-center gap-3">
      <Reveal>
        <span className="inline-flex items-center rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/90 backdrop-blur">{badge}</span>
      </Reveal>
      {ral && (
        <Reveal delay={0.05}>
          <span className="inline-flex items-center rounded-full bg-green px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white">RAL</span>
        </Reveal>
      )}
    </div>
  );

  const LogoOrName = logo ? (
    <Reveal delay={0.08}>
      <div className={`${split ? "mt-5" : "mt-7"} inline-flex items-center rounded-2xl bg-white px-7 py-5 lg:px-10 lg:py-7 shadow-[0_28px_64px_-24px_rgba(0,0,0,0.55)]`} data-testid={`${testid}-logo`}>
        <img src={logo} alt={name} className="h-11 lg:h-16 w-auto" />
      </div>
    </Reveal>
  ) : (
    <h1 className={`${split ? "mt-5" : "mt-6"} font-serif text-5xl lg:text-7xl font-bold text-white tracking-tight`}>{name}</h1>
  );

  const Texts = (
    <>
      {tagline && (
        <Reveal delay={0.15}>
          <p className={`${split ? "" : "mt-6"} text-lg text-white/70 max-w-2xl leading-relaxed`}>{tagline}</p>
        </Reveal>
      )}
      {paragraphs?.length > 0 && (
        <Reveal delay={0.2}>
          <div className={`${split ? "mt-4" : "mt-5"} max-w-2xl space-y-3 text-[15px] leading-relaxed text-white/60`}>
            {paragraphs.map((p, i) => (<p key={i}>{p}</p>))}
          </div>
        </Reveal>
      )}
    </>
  );

  return (
    <section
      className={`relative overflow-hidden grain-overlay ${split ? "pt-[104px] lg:pt-[120px] pb-12 lg:pb-14" : "pt-[120px] lg:pt-[150px] pb-16 lg:pb-24"}`}
      style={{ background: theme === "green" ? "#1F5C48" : "#12284C" }}
      data-testid={testid}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-[120px] opacity-40" style={{ background: theme === "green" ? "#2E8B6B" : "#1B3A6B" }} />
      <div className="container-x relative">
        <Reveal>
          <Link to="/prodotti" className="text-xs font-medium text-white/50 hover:text-white transition-colors">{COPY.ui.allProducts}</Link>
        </Reveal>

        {split ? (
          <>
            {logo && <h1 className="sr-only">{name}</h1>}
            <div className="mt-6 grid gap-6 lg:grid-cols-2 lg:items-center lg:gap-14">
              <div className="flex flex-col items-start">
                {Badges}
                {LogoOrName}
                {leftExtra}
              </div>
              <div>{rightContent || Texts}</div>
            </div>
          </>
        ) : (
          <>
            <div className="mt-6">{Badges}</div>
            {logo && <h1 className="sr-only">{name}</h1>}
            {LogoOrName}
            {Texts}
          </>
        )}
      </div>
    </section>
  );
};

export const ProductCard = ({ p, theme = "green" }) => {
  const { COPY } = useContent();
  return (
    <StaggerItem>
      <div className="card-clean h-full p-8 flex flex-col" data-testid={`product-${p.id}`}>
        <div className="flex items-start justify-between">
          <h3 className="font-serif text-2xl font-bold text-navy">{p.name}</h3>
          {p.badge && (
            <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white ${theme === "green" ? "bg-green-deep" : "bg-navy-deep"}`}>{p.badge}</span>
          )}
        </div>
        {p.type && <span className="mt-1 text-xs font-semibold uppercase tracking-wider text-green">{p.type}</span>}
        {p.available && (
          <span className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-green/12 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-green-deep" data-testid={`product-available-${p.id}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-green animate-pulse" />
            {COPY.ui.immediateAvailability}
          </span>
        )}
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
        {p.variants && (
          <div className="mt-4 flex flex-wrap gap-2">
            {p.variants.map((v) => (
              <span key={v} className={`rounded-full px-3 py-1 text-xs font-semibold ${theme === "green" ? "bg-green/10 text-green-deep" : "bg-navy/8 text-navy"}`}>{v}</span>
            ))}
          </div>
        )}
        {p.features && (
          <ul className="mt-5 space-y-2.5">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-navy/80">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green/12 text-green">
                  <Check size={12} strokeWidth={3} />
                </span>
                {f}
              </li>
            ))}
          </ul>
        )}
      </div>
    </StaggerItem>
  );
};

export const RequestInfo = () => {
  const { COPY } = useContent();
  return (
    <Reveal className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-3xl border border-[#E7EDF2] bg-cloud p-8 lg:p-10">
      <div>
        <h3 className="font-serif text-2xl font-bold text-navy">{COPY.ui.requestTitle}</h3>
        <p className="mt-2 text-muted-foreground">{COPY.ui.requestText}</p>
      </div>
      <Link to="/contatti" data-testid="request-info-cta" className="btn-green shrink-0">
        {COPY.ui.requestCta} <ArrowRight size={16} />
      </Link>
    </Reveal>
  );
};

const _ProductParts = { ProductHero, ProductCard, RequestInfo };
export default _ProductParts;
